import React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { getLocalizedUseCase } from '../../content/useCases'
import type { UseCaseEntry } from '../../types/useCase'
import { useLanguage } from '../../i18n/LanguageContext'
import { trackLandingEvent, withStoreUtm } from '../../services/analytics'

const PageContainer = styled.article`
  padding: 50px 24px 50px;
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 48px;
`

const Breadcrumb = styled.nav`
  font-size: 0.9rem;
  color: var(--color-text-muted);
  display: flex;
  gap: 8px;
  align-items: center;

  a {
    color: var(--color-accent);
    text-decoration: none;
  }
`

const Hero = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
`

const Eyebrow = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-accent);
`

const HeroTitle = styled.h1`
  font-size: clamp(2rem, 4vw, 3.2rem);
  line-height: 1.2;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
`

const HeroSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  line-height: 1.7;
  margin: 0;
`

const QuickMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  color: var(--color-accent);
  font-weight: 600;
  font-size: 0.95rem;
`

const TopCTASection = styled.section`
  background: linear-gradient(135deg, rgba(63, 118, 255, 0.12), rgba(140, 214, 255, 0.12));
  border: 1px solid var(--color-border);
  border-radius: var(--radius-large);
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: var(--shadow-soft);
`

const TopCTAHeading = styled.h2`
  margin: 0;
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--color-text-primary);
`

const TopCTAText = styled.p`
  margin: 0;
  font-size: 1rem;
  color: var(--color-text-secondary);
  line-height: 1.6;
`

const TopCTAButton = styled.a`
  align-self: flex-start;
  padding: 12px 24px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  background: linear-gradient(135deg, var(--color-accent), var(--color-accent-dark));
  color: white;
  box-shadow: var(--shadow-medium);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
  }
`

const MetricsGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`

const MetricCard = styled.div`
  padding: 24px;
  border-radius: var(--radius-medium);
  background: linear-gradient(135deg, rgba(63, 118, 255, 0.1), rgba(140, 214, 255, 0.1));
  border: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const MetricValue = styled.span`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text-primary);
`

const MetricLabel = styled.span`
  font-size: 0.95rem;
  color: var(--color-accent);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`

const MetricContext = styled.p`
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.6;
`

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SectionTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
`

const Paragraph = styled.p`
  font-size: 1rem;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.8;
`

const Highlights = styled.ul`
  margin: 0;
  padding-left: 24px;
  color: var(--color-text-primary);
  display: flex;
  flex-direction: column;
  gap: 12px;

  li {
    line-height: 1.6;
  }
`

const WorkflowList = styled.ol`
  margin: 0;
  padding-left: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: var(--color-text-primary);

  li {
    line-height: 1.7;
  }
`

const QuoteSection = styled.blockquote`
  margin: 0;
  padding: 32px;
  border-radius: var(--radius-large);
  background: var(--color-accent-soft);
  border: 1px solid var(--color-border);
  font-size: 1.1rem;
  line-height: 1.7;
  color: var(--color-text-secondary);
  font-style: italic;
`

const QuoteAuthor = styled.cite`
  display: block;
  margin-top: 16px;
  font-style: normal;
  color: var(--color-accent);
  font-weight: 600;
`

const NotFound = styled.div`
  padding: 180px 24px;
  text-align: center;
  color: #4a5568;
  display: flex;
  flex-direction: column;
  gap: 16px;

  h1 {
    font-size: 2.4rem;
    margin: 0;
    color: #1a202c;
  }
`

const chromeWebStoreUrl = withStoreUtm('use_case_article_top')

interface UseCaseArticleProps {
  entry?: UseCaseEntry
}

const UseCaseArticle: React.FC<UseCaseArticleProps> = ({ entry }) => {
  const { language, t } = useLanguage()

  if (!entry) {
    return (
      <NotFound>
        <h1>{t('seo.useCaseFallbackTitle')}</h1>
        <p>{t('seo.useCaseFallbackDescription')}</p>
        <Link to="/solutions">{t('useCases.backToList')}</Link>
      </NotFound>
    )
  }

  const useCase = getLocalizedUseCase(entry, language)
  const quoteSeparator = language === 'zh' || language === 'ja' ? '、' : ', '
  const keywordsPreview = useCase.keywords.slice(0, 3).join(' / ')

  return (
    <PageContainer>
      <TopCTASection>
        <TopCTAHeading>{t('useCases.ctaTitle')}</TopCTAHeading>
        <TopCTAText>{t('useCases.ctaText')}</TopCTAText>
        <TopCTAButton
          href={chromeWebStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackLandingEvent('store_cta_click', { placement: 'use_case_article_top', slug: entry.slug })}
        >
          {t('useCases.ctaButton')}
        </TopCTAButton>
      </TopCTASection>
      <Breadcrumb>
        <Link to="/solutions">{t('navigation.solutions')}</Link>
        <span>/</span>
        <span>{useCase.industry}</span>
      </Breadcrumb>
      <Hero>
        <Eyebrow>
          {useCase.heroEyebrow} · {useCase.industry}
        </Eyebrow>
        <HeroTitle>{useCase.title}</HeroTitle>
        <HeroSubtitle>{useCase.subtitle}</HeroSubtitle>
        <QuickMeta>
          <span>{useCase.readingTime}</span>
          <span>
            {t('useCases.keywordsPrefix')}
            {keywordsPreview}
          </span>
        </QuickMeta>
      </Hero>
      <MetricsGrid>
        {useCase.metrics.map((metric) => (
          <MetricCard key={metric.label}>
            <MetricLabel>{metric.label}</MetricLabel>
            <MetricValue>{metric.value}</MetricValue>
            <MetricContext>{metric.context}</MetricContext>
          </MetricCard>
        ))}
      </MetricsGrid>
      {useCase.sections.map((section) => (
        <Section key={section.title}>
          <SectionTitle>{section.title}</SectionTitle>
          {section.paragraphs.map((paragraph, index) => (
            <Paragraph key={index}>{paragraph}</Paragraph>
          ))}
        </Section>
      ))}
      <Section>
        <SectionTitle>{t('useCases.highlightsTitle')}</SectionTitle>
        <Highlights>
          {useCase.highlights.map((highlight, index) => (
            <li key={index}>{highlight}</li>
          ))}
        </Highlights>
      </Section>
      <Section>
        <SectionTitle>{t('useCases.workflowTitle')}</SectionTitle>
        <WorkflowList>
          {useCase.workflow.map((step) => (
            <li key={step.title}>
              <strong>{step.title}</strong>
              {language === 'zh' || language === 'ja' ? '：' : ': '}
              {step.description}
            </li>
          ))}
        </WorkflowList>
      </Section>
      <QuoteSection>
        “
        {useCase.quote.text}
        ”
        <QuoteAuthor>
          — {useCase.quote.author}
          {quoteSeparator}
          {useCase.quote.role}
        </QuoteAuthor>
      </QuoteSection>
    </PageContainer>
  )
}

export default UseCaseArticle
